/** 
  All of the routes for the Material Dashboard 3 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 3 PRO React layouts
import Analytics from "layouts/dashboards/analytics";
import Sales from "layouts/dashboards/sales";
import ProfileOverview from "layouts/pages/profile/profile-overview";
import AllProjects from "layouts/pages/profile/all-projects";
import NewUser from "layouts/pages/users/new-user";
import Settings from "layouts/pages/account/settings";
import Billing from "layouts/pages/account/billing";
import Invoice from "layouts/pages/account/invoice";
import Timeline from "layouts/pages/projects/timeline";
import PricingPage from "layouts/pages/pricing-page";
import Widgets from "layouts/pages/widgets";
import RTL from "layouts/pages/rtl";
import Charts from "layouts/pages/charts";
import Notifications from "layouts/pages/notifications";
import Kanban from "layouts/applications/kanban";
import Wizard from "layouts/applications/wizard";
import DataTables from "layouts/applications/data-tables";
import Calendar from "layouts/applications/calendar";
import NewProduct from "layouts/ecommerce/products/new-product";
import EditProduct from "layouts/ecommerce/products/edit-product";
import ProductPage from "layouts/ecommerce/products/product-page";
import OrderList from "layouts/ecommerce/orders/order-list";
import OrderDetails from "layouts/ecommerce/orders/order-details";
import SignInBasic from "layouts/authentication/sign-in/basic";
import SignInCover from "layouts/authentication/sign-in/cover";
import SignInIllustration from "layouts/authentication/sign-in/illustration";
import SignUpCover from "layouts/authentication/sign-up/cover";
import ResetCover from "layouts/authentication/reset-password/cover";

// Material Dashboard 3 PRO React components
import MDAvatar from "components/MDAvatar";

// @mui icons
import Icon from "@mui/material/Icon";

// Images
import profilePicture from "assets/images/team-3.jpg";
import CookiebotIntegration from "layouts/pages/cmpbanner";
import GoogleConsentMode from "layouts/pages/googleconsent";
import BannerLayout from "layouts/pages/bannerlayout";
import BannerDialog from "layouts/pages/bannerdialog";
import BannerDesign from "layouts/pages/bannerdesign";
import BannerLanguage from "layouts/pages/bannerlanguage";
import Maintenance from "components/Maintenance";
import AccountSettings from "layouts/pages/account-settings";
import AccountPurchase from "layouts/pages/account-purchase";
import BillingSettings from "layouts/pages/billingsettings";
import ProfileSettings from "layouts/pages/profile-settings";
import InvoiceSettings from "layouts/pages/invoice-settings";
import Dashboard from "layouts/dashboards/dashboard";
import Domains from "layouts/pages/domains";
import CookieCategories from "layouts/pages/cookies-categories";
import ConsentReport from "layouts/pages/consent-report";
import UserManagement from "layouts/management/users";

// Resolve client name from cached /me response in sessionStorage
const USER_DATA_KEY = "userMeData";
const getCachedUserData = () => {
  try {
    const raw = typeof window !== "undefined" ? sessionStorage.getItem(USER_DATA_KEY) : null;
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const __cachedUser = getCachedUserData();
const rawClientName = (
  __cachedUser?.user?.clients?.find?.((c) => c?.isActive)?.name ||
  __cachedUser?.user?.clients?.[0]?.name ||
  "Logicookie Ltd."
);
const __activeClientName = rawClientName.length > 18 ? rawClientName.slice(0, 15) + "..." : rawClientName;

const routes = [
  {
    type: "collapse",
    name: __activeClientName,
    key: "company",
    icon: <Icon fontSize="small">business</Icon>,
    collapse: [
      {
        name: "Hesap Bilgileri",
        key: "account-settings",
        route: "/company/account-settings",
        component: <AccountSettings />,
      },
      {
        name: "Üyelik Bilgileri",
        key: "profile-settings",
        route: "/company/profile-settings",
        component: <ProfileSettings />,
      },
      {
        name: "Satın Alma",
        key: "account-purchase",
        route: "/company/account-purchase",
        component: <AccountPurchase />,
      },
      {
        name: "Fatura Bilgileri",
        key: "billing-settings",
        route: "/company/billing-settings",
        component: <BillingSettings />,
      },
      {
        name: "Satın Geçmişi",
        key: "invoice-settings",
        route: "/company/invoice-settings",
        component: <InvoiceSettings />,
      }
    ],
  },
  { type: "divider", key: "divider-0" },
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    route: "/dashboards",
    icon: <Icon fontSize="small">dashboard</Icon>,
    component: <Dashboard />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Domainler & Web Siteleri",
    key: "domains",
    route: "/domains",
    icon: <Icon fontSize="small">tab</Icon>,
    component: <Domains />,
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Entegrasyon",
    key: "entegrations",
    icon: <Icon fontSize="small">integration_instructions</Icon>,
    collapse: [
      {
        name: "Çerez Onay Bildirimi",
        key: "cmp-banner",
        route: "/integrations/cmp-banner",
        component: <CookiebotIntegration />,
      },
      {
        name: "Google Consent",
        key: "google-consent",
        route: "/integrations/google-consent",
        component: <GoogleConsentMode />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Özelleştirme",
    key: "configuration",
    icon: <Icon fontSize="small">format_paint</Icon>,
    collapse: [
      {
        name: "Düzen",
        key: "layout",
        route: "/configuration/layout",
        component: <BannerLayout/>,
      },
      {
        name: "Tasarım",
        key: "design",
        route: "/configuration/design",
        component: <BannerDesign />,
      },
      {
        name: "Dil Ayarları",
        key: "language",
        route: "/configuration/language",
        component: <BannerLanguage />,
      },
      {
        name: "Diyaloglar",
        key: "content-dialogs",
        route: "/configuration/content-dialogs",
        component: <BannerDialog />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Çerezler",
    key: "cookies",
    icon: <Icon fontSize="small">cookie</Icon>,
    collapse: [
      {
        name: "Kategoriler",
        key: "settings",
        route: "/cookies/categories",
        component: <CookieCategories />,
      },
      // {
      //   name: "Tarama Geçmişi",
      //   key: "scan-history",
      //   route: "/cookies/scan-history",
      //   component: <Maintenance />,
      // },
    ],
  },
  {
    type: "collapse",
    name: "İstatistik",
    key: "report",
    route: "/consent-report",
    icon: <Icon fontSize="small">analytics</Icon>,
    component: <ConsentReport />,
    noCollapse: true
  },
  {
    type: "collapse",
    name: "Yönetim",
    key: "management",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    roles: ["admin", "superadmin"],
    collapse: [
      {
        name: "Kullanıcılar",
        key: "management-users",
        route: "/management/users",
        component: <UserManagement />,
        roles: ["admin", "superadmin"],
      },
    ],
  },
];

export default routes;
